import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import type { BdrLead, BdrFiltersAll } from '@/types'
import { AllLeadsSkeletonRow } from '@/components/BdrPanelV2/bdrAllLeadsTableUtils'
import { BdrAllLeadsTableToolbar } from '@/components/BdrPanelV2/BdrAllLeadsTableToolbar'
import { BdrAllLeadsTableRows } from '@/components/BdrPanelV2/BdrAllLeadsTableRows'

interface BdrAllLeadsTableProps {
  filteredAllLeads: BdrLead[]
  filters: BdrFiltersAll
  openLeadDetail: (lead: BdrLead) => void
  openRegTentativaModal: (lead: BdrLead) => void
  loading: boolean
  isFiltering: boolean
  loadingMore: boolean
  hasMore: boolean
  totalLeads: number
  handleLoadMore: (page: number) => void
}

export function BdrAllLeadsTable({
  filteredAllLeads,
  filters,
  openLeadDetail,
  openRegTentativaModal,
  loading,
  isFiltering,
  loadingMore,
  hasMore,
  totalLeads,
  handleLoadMore,
}: BdrAllLeadsTableProps) {
  const [localSearch, setLocalSearch] = useState(filters.search)

  useEffect(() => {
    const timer = setTimeout(() => filters.setSearch(localSearch), 300)
    return () => clearTimeout(timer)
  }, [localSearch, filters])

  const totalPages = Math.max(1, Math.ceil(totalLeads / 30))

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <BdrAllLeadsTableToolbar localSearch={localSearch} setLocalSearch={setLocalSearch} filters={filters} />

      <div className="glass-card border-white/[0.06] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="text-left px-3 py-2.5 text-text-muted font-medium uppercase tracking-wider text-[10px]">
                  Lead / Empresa
                </th>
                <th className="text-left px-3 py-2.5 text-text-muted font-medium uppercase tracking-wider text-[10px]">
                  Contato
                </th>
                <th className="text-left px-3 py-2.5 text-text-muted font-medium uppercase tracking-wider text-[10px]">
                  Status / Fase
                </th>
                <th className="text-left px-3 py-2.5 text-text-muted font-medium uppercase tracking-wider text-[10px]">
                  Temperatura
                </th>
                <th className="text-left px-3 py-2.5 text-text-muted font-medium uppercase tracking-wider text-[10px]">
                  Entrada
                </th>
                <th className="text-right px-3 py-2.5 text-text-muted font-medium uppercase tracking-wider text-[10px]">
                  Acoes
                </th>
              </tr>
            </thead>
            <tbody>
              {loading || isFiltering ? (
                <>
                  <AllLeadsSkeletonRow />
                  <AllLeadsSkeletonRow />
                  <AllLeadsSkeletonRow />
                  <AllLeadsSkeletonRow />
                  <AllLeadsSkeletonRow />
                </>
              ) : filteredAllLeads.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-text-muted">
                    Nenhum lead encontrado.
                  </td>
                </tr>
              ) : (
                <BdrAllLeadsTableRows
                  leads={filteredAllLeads}
                  openLeadDetail={openLeadDetail}
                  openRegTentativaModal={openRegTentativaModal}
                />
              )}
            </tbody>
          </table>
        </div>

        {!loading && filteredAllLeads.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.04]">
            <button
              onClick={() => handleLoadMore(filters.page - 1)}
              disabled={filters.page <= 1 || loadingMore}
              className="text-xs text-text-muted hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors min-h-[44px] px-3"
            >
              Anterior
            </button>
            <span className="text-text-muted text-xs">
              Pagina {filters.page} de {totalPages} ({totalLeads} total)
            </span>
            <button
              onClick={() => handleLoadMore(filters.page + 1)}
              disabled={!hasMore || loadingMore}
              className="text-xs text-text-muted hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors min-h-[44px] px-3 flex items-center gap-1.5"
            >
              {loadingMore && <Loader2 size={12} className="animate-spin" />}
              Proximo
            </button>
          </div>
        )}

        {!loading && (
          <div className="px-4 py-2 border-t border-white/[0.04] text-text-muted text-[10px]">
            Mostrando {filteredAllLeads.length} de {totalLeads} leads
          </div>
        )}
      </div>
    </motion.div>
  )
}
